import moment from 'moment'

import { toWallClock } from '@/helpers/toWallClock'

import type { FlightRequestDetail, FlightRoute } from '../interfaces/tripDetail'

// Las tres funciones de abajo pintan WALL CLOCKS del lugar del servicio, no
// instantes: el check-in es el día 18 en el hotel y el vuelo sale 10:30 en el
// aeropuerto, vea el viajero la pantalla desde donde la vea. `toWallClock`
// deshace el falso instante que serializa el backend; ver ese helper.

// Fecha de calendario larga en español: "12 de abril de 2026". Sin hora, porque
// un check-in/check-out de hotel es una fecha, no un momento del día.
export function formatDate(d: string | null): string {
	if (!d) return '—'
	return moment(toWallClock(d)).locale('es').format('D [de] MMMM [de] YYYY')
}

// Fecha + hora: "12 de abril de 2026 a las 14:30" (salida de vuelo, recogida de
// auto/autobús).
export function formatDateTime(d: string | null): string {
	if (!d) return '—'
	return moment(toWallClock(d))
		.locale('es')
		.format('D [de] MMMM [de] YYYY [a las] HH:mm ')
}

// Hora en formato 24h
export function formatTime(d: string): string {
	return moment(toWallClock(d)).format('HH:mm')
}

// Convierte un monto numérico o string a moneda localizada
export function formatCurrency(
	amount: number | string,
	currency?: string
): string {
	const num = typeof amount === 'string' ? parseFloat(amount) : amount
	return new Intl.NumberFormat('es-MX', {
		style: 'currency',
		currency: currency || 'MXN',
		minimumFractionDigits: 2,
	}).format(Number.isFinite(num) ? num : 0)
}

// Convierte minutos a "Xh Ym"
export function formatDuration(mins: number | null): string {
	if (!mins) return ''
	const h = Math.floor(mins / 60)
	const m = mins % 60
	return h > 0 ? `${h}h ${m}m` : `${m}m`
}

// Calcula ruta ida / vuelta a partir del detalle de vuelo
export function getFlightRoute(req: FlightRequestDetail): FlightRoute {
	const isRoundTrip =
		req.origin === req.destination && !!req.flightDetails?.outboundDestination
	return {
		from: req.origin,
		to: isRoundTrip ? req.flightDetails!.outboundDestination! : req.destination,
		roundTrip: isRoundTrip,
	}
}

// Badge de estado de la solicitud (Confirmado, Cancelado, etc.)
export function statusClass(s: string): string {
	const map: Record<string, string> = {
		Confirmado: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
		Cancelado: 'bg-red-100 text-red-800 border border-red-200',
		'En Proceso': 'bg-amber-100 text-amber-800 border border-amber-200',
		Creado: 'bg-blue-100 text-blue-800 border border-blue-200',
	}
	return map[s] ?? 'bg-gray-100 text-gray-600'
}

// Traduce el estado de un boleto al español
export function ticketStatusLabel(s: string | null): string {
	const map: Record<string, string> = {
		issued: 'Emitido',
		pending: 'Pendiente',
		pending_manual: 'Emisión Manual',
		voided: 'Anulado',
	}
	return map[s ?? ''] ?? s ?? ''
}

export function ticketStatusClass(s: string | null): string {
	const map: Record<string, string> = {
		issued: 'bg-emerald-100 text-emerald-800',
		pending: 'bg-amber-100 text-amber-800',
		pending_manual: 'bg-red-100 text-red-800',
		voided: 'bg-gray-100 text-gray-600',
	}
	return map[s ?? ''] ?? 'bg-gray-100 text-gray-600'
}

// Traduce el tipo de pasajero (ADT, CNN, INF)
export function paxTypeLabel(t: string | null): string {
	const map: Record<string, string> = {
		ADT: 'Adulto',
		CNN: 'Niño',
		INF: 'Infante',
	}
	return map[t ?? ''] ?? t ?? ''
}

// IDs del catálogo de estados (/api/status). Solo existen estos tres; `stateName`
// es un label de display que puede diverger del catálogo (p. ej. "Pendiente Pago"
// o "En proceso" para un request en `created`), así que el gate se hace por
// `stateId`, nunca por el string.
export const REQUEST_STATE = {
	created: 1,
	confirmed: 2,
	cancelled: 3,
} as const

// True si el request tiene una reserva confirmada (existe algo real que
// descargar/reenviar). Se evalúa por `stateId`: solo `confirmed` cuenta; created
// (incluidas sus variantes de display como "Pendiente Pago") y cancelled → false.
export function isBooked(stateId: number | null | undefined): boolean {
	return stateId === REQUEST_STATE.confirmed
}

// El request en "Creado" todavía no tiene tarifa real: 0 significa "sin
// cotizar", no "gratis". Se evalúa por `stateId === created` explícito (no
// `!== confirmed`) para no atrapar `cancelled`: un servicio cancelado sin
// tarifa muestra su monto real (0), no "Cotizando".
export function isQuotingFare(
	stateId: number | null | undefined,
	fare: string | number | null | undefined
): boolean {
	return stateId === REQUEST_STATE.created && (Number(fare) || 0) === 0
}

// `clientFare` viene en la moneda local del servicio; `currencyConversion` la
// lleva a la moneda base (MXN) — el mismo cálculo que hace el backend en
// `Request::total` (BookingV2 app/Models/Request.php), pero ahí ese accessor
// es null para vuelos, así que sumar `total` cruzado da 0 falso. Se replica
// aquí para que el front nunca dependa de ese accessor.
export function baseFareOf(
	clientFare: string | number | null | undefined,
	currencyConversion: string | number | null | undefined
): number {
	return (parseFloat(String(clientFare)) || 0) * (parseFloat(String(currencyConversion)) || 1)
}

// Diferencia en noches entre dos fechas (para hotel)
export function nightsBetween(start: string, end: string): number {
	if (!start || !end) return 0
	return Math.max(0, moment(end).diff(moment(start), 'days'))
}
